import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import {
  applyThemeButtonGradientStyles,
  darkenThemeHexColor,
  getLandingPageTitleColor,
  getThemeButtonGradient,
  getThemeButtonTextColor,
  normalizeThemeHexColor,
} from "../../functions/themeAssets";
import { isThemeFlagOn } from "../gameSetupStorage";
import { getBinTossItems } from "../../functions/binToss";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";

function readSessionUserId() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return "";
    const u = JSON.parse(raw);
    const id = u?.userId ?? u?.userid ?? u?.id;
    if (id == null || id === "") return "";
    return String(id).trim();
  } catch {
    return "";
  }
}

function normalizeLeaderboardRow(row, index) {
  if (!row || typeof row !== "object") {
    return {
      key: `row-${index}`,
      rank: index + 1,
      userId: "",
      name: "—",
      points: 0,
      time: "00:00",
    };
  }
  const name =
    row.name ??
    row.player_name ??
    row.user_name ??
    row.display_name ??
    (row.email ? String(row.email).split("@")[0] : null) ??
    "—";
  const rawPoints = row.points ?? row.score ?? row.total_score ?? row.point ?? 0;
  const n = Number(rawPoints);
  const points = Number.isFinite(n) ? n : 0;
  const rawTime = row.time ?? row.display_time ?? "00:00";
  const time = String(rawTime).trim() || "00:00";
  const rankNum = Number(row.rank ?? row.rk);
  const rank = Number.isFinite(rankNum) && rankNum > 0 ? rankNum : index + 1;
  const userId =
    row.userId != null && row.userId !== ""
      ? String(row.userId)
      : row.userid != null && row.userid !== ""
        ? String(row.userid)
        : "";

  return {
    key: String(row.userId ?? row.userid ?? row.rank ?? row.id ?? index),
    rank,
    userId,
    name: String(name),
    points,
    time,
  };
}

const LEADERBOARD_TOP_N = 5;

function buildDisplayRows(rows, currentUserId) {
  if (!rows.length) return [];

  const sorted = [...rows].sort((a, b) => a.rank - b.rank);
  const top = sorted
    .filter((r) => r.rank >= 1 && r.rank <= LEADERBOARD_TOP_N)
    .slice(0, LEADERBOARD_TOP_N);

  if (!currentUserId) return top;

  const userRow = sorted.find(
    (r) => r.userId && String(r.userId) === String(currentUserId)
  );

  if (!userRow || userRow.rank <= LEADERBOARD_TOP_N) return top;

  return [...top, { ...userRow, isPinnedViewer: true }];
}


function TrophyIcon() {
  return (
    <svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">
      <defs>
        <linearGradient id="lbTrophyGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff1a8" />
          <stop offset="45%" stopColor="#f7c23c" />
          <stop offset="100%" stopColor="#c98a12" />
        </linearGradient>
      </defs>
      <path
        d="M18 10h-8c0 10 4 16 11 17M46 10h8c0 10-4 16-11 17"
        fill="none"
        stroke="#e0a526"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <path d="M17 6h30v12c0 10-7 17-15 17S17 28 17 18V6z" fill="url(#lbTrophyGold)" />
      <path d="M28 34h8v9h-8z" fill="#d49a1c" />
      <path d="M21 43h22l3 9H18l3-9z" fill="url(#lbTrophyGold)" />
      <rect x="15" y="52" width="34" height="6" rx="2" fill="#b57a10" />
      <ellipse cx="32" cy="17" rx="4" ry="6" fill="#fff" opacity="0.85" />
    </svg>
  );
}

/* One laurel branch; the right-hand one is mirrored in CSS. */
function LaurelIcon({ className }) {
  const leaves = [
    [27, 68, -35],
    [20, 57, -25],
    [16, 45, -12],
    [15, 32, 0],
    [18, 20, 12],
  ];
  return (
    <svg className={className} viewBox="0 0 40 80" width="40" height="80" aria-hidden="true">
      <path d="M31 76C12 62 9 34 22 8" fill="none" stroke="#e0a526" strokeWidth="2.5" strokeLinecap="round" />
      {leaves.map(([x, y, r]) => (
        <g key={`${x}-${y}`} fill="#f2b632">
          <ellipse cx={x - 6} cy={y} rx="3.4" ry="7.5" transform={`rotate(${r - 45} ${x - 6} ${y})`} />
          <ellipse cx={x + 5} cy={y - 3} rx="3" ry="6.5" transform={`rotate(${r + 40} ${x + 5} ${y - 3})`} />
        </g>
      ))}
      <ellipse cx="22" cy="7" rx="3" ry="6.5" fill="#f2b632" transform="rotate(20 22 7)" />
    </svg>
  );
}

const MEDAL_CLASS = { 1: "gold", 2: "silver", 3: "bronze" };

/** Metallic medal for the podium, plain number for everyone else. */
function RankMedal({ rank }) {
  const metal = MEDAL_CLASS[rank];
  if (!metal) {
    return (
      <span className="lb-rank-num" aria-label={`Rank ${rank}`}>
        {rank}
      </span>
    );
  }
  return (
    <span className={`lb-medal lb-medal--${metal}`} aria-label={`Rank ${rank}`}>
      {rank}
    </span>
  );
}

function hexToRgba(hex, alpha) {
  const h = String(hex || "").replace("#", "");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((c) => Number.isNaN(c))) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Admin theme → leaderboard colors: the header bar, "you" row, badge and points pill
 *  follow the button gradient; the title and row text use the admin "Text Color"
 *  (landing_page_title_color). Anything unset falls back to the CSS defaults. */
function useLeaderboardThemeVars(themeData) {
  return useMemo(() => {
    const vars = {};
    const { start, end } = getThemeButtonGradient(themeData);
    if (start) {
      const resolvedEnd = end || darkenThemeHexColor(start, 0.25) || start;
      vars["--lb-accent-start"] = start;
      vars["--lb-accent-end"] = resolvedEnd;
      vars["--lb-you-bg"] = hexToRgba(start, 0.14);
      vars["--lb-you-ring"] = hexToRgba(start, 0.55);
    }
    const buttonText = normalizeThemeHexColor(getThemeButtonTextColor(themeData));
    if (buttonText) vars["--lb-on-accent"] = buttonText;
    const text = normalizeThemeHexColor(getLandingPageTitleColor(themeData));
    if (text) {
      vars["--lb-text"] = text;
      vars["--lb-text-soft"] = hexToRgba(text, 0.65);
    }
    return vars;
  }, [themeData]);
}

function Leaderboard() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData, status: themeStatus } = useSelector(
    (s) => s.theme
  );
  const themeVars = useLeaderboardThemeVars(themeData);

  // Leaderboard off → the score card also carries the admin thank-you message (Rules page).
  const thankYouText = String(themeData?.custom_text_thank_you_page ?? "").trim();
  const thankYouColor = normalizeThemeHexColor(themeData?.ty_Textcolor) || undefined;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(() => readSessionUserId());

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  // Admin "Show Leaderboard" off → only the player's own score, no ranking.
  const showRanking = isThemeFlagOn(themeData?.leaderboard, true);
  const themeReady = Boolean(themeData) || themeStatus === "failed";

  // Thank-you page passes the score along; /fetchReport below refreshes it.
  const navState = useLocation().state;
  const [myScore, setMyScore] = useState(() => ({
    points: typeof navState?.points === "number" ? navState.points : null,
    time: navState?.time ? String(navState.time) : "",
  }));
  const maxPoints = useMemo(() => {
    const perCorrect = Math.max(1, parseInt(themeData?.points, 10) || 1);
    return getBinTossItems(themeData).length * perCorrect;
  }, [themeData]);

  useEffect(() => {
    if (!themeReady || showRanking) return;
    const uid = readSessionUserId();
    if (!uid || !adminToken || !backendBase) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: uid }),
        });
        const data = await res.json().catch(() => ({}));
        const r = res.ok && data.success ? data.report : null;
        if (!cancelled && r) {
          setMyScore((prev) => ({
            points: r.points != null ? Number(r.points) || 0 : prev.points,
            time: r.time != null && String(r.time).trim() ? String(r.time).trim() : prev.time,
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [themeReady, showRanking, adminToken, backendBase]);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (themeStatus === "succeeded" && themeData) {
      applyThemeButtonGradientStyles(themeData);
    }
  }, [themeStatus, themeData]);

  useEffect(() => {
    document.body.classList.add("leaderboard-page");
    return () => {
      document.body.classList.remove("leaderboard-page");
    };
  }, []);

  const loadLeaderboard = useCallback(async () => {
    if (!adminToken || !backendBase) {
      setLoading(false);
      setError(!adminToken ? "Not signed in." : "Server URL not configured.");
      return;
    }
    setLoading(true);
    setError(null);
    const uid = readSessionUserId();
    setCurrentUserId(uid);
    try {
      const res = await fetch(`${backendBase}/getLeaderboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid || undefined,
          limit: LEADERBOARD_TOP_N,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && Array.isArray(data.data)) {
        setRows(data.data.map(normalizeLeaderboardRow));
      } else {
        setError(data.error || data.message || "Failed to load leaderboard.");
      }
    } catch (e) {
      setError(e.message || "Failed to load leaderboard.");
    } finally {
      setLoading(false);
    }
  }, [adminToken, backendBase]);

  useEffect(() => {
    if (themeReady && showRanking) loadLeaderboard();
  }, [loadLeaderboard, themeReady, showRanking]);

  const displayRows = useMemo(
    () => buildDisplayRows(rows, currentUserId),
    [rows, currentUserId]
  );

  return (
    <div className="lb-page" style={themeVars}>
      <div className="lb-shell">
        <header className="lb-hero">
          <span className="lb-hero__trophy">
            <TrophyIcon />
          </span>
          <div className="lb-hero__row">
            <LaurelIcon className="lb-laurel lb-laurel--left" />
            <div className="lb-hero__text">
              <h1 className="lb-hero__title">{showRanking ? "Leaderboard" : "Your Score"}</h1>
            </div>
            <LaurelIcon className="lb-laurel lb-laurel--right" />
          </div>
        </header>

        {/* Wait for the theme: until it loads the flag reads as "on", which would
            flash the ranking table for themes that have the leaderboard turned off. */}
        {!themeReady ? (
          <p className="lb-status">Loading…</p>
        ) : !showRanking ? (
          <section className="lb-solo-card" aria-label="Your score">
            {loading ? (
              <p className="lb-status">Loading your score…</p>
            ) : myScore.points == null ? (
              <p className="lb-status">No score found yet.</p>
            ) : (
              <p className="lb-solo">
                Your score is{" "}
                <strong className="lb-solo__value">{myScore.points.toLocaleString()}</strong>{" "}
                out of <strong className="lb-solo__value">{maxPoints.toLocaleString()}</strong>
                {myScore.time ? (
                  <>
                    {" "}in <strong className="lb-solo__value">{myScore.time}</strong> minutes
                  </>
                ) : null}
                .
              </p>
            )}
            {thankYouText ? (
              <div className="lb-thankyou" style={thankYouColor ? { color: thankYouColor } : undefined}>
                <p className="lb-thankyou__title">{thankYouText}</p>
              </div>
            ) : null}
          </section>
        ) : (
          <section className="lb-board" aria-label="Top scores">
            <div className="lb-board__head" aria-hidden>
              <span className="lb-col lb-col--rank">Rank</span>
              <span className="lb-col lb-col--name">Name</span>
              <span className="lb-col lb-col--time">Time</span>
              <span className="lb-col lb-col--points">Points</span>
            </div>
  
            {loading ? (
              <p className="lb-status">Loading scores…</p>
            ) : error ? (
              <p className="lb-status lb-status--error">{error}</p>
            ) : rows.length === 0 ? (
              <p className="lb-status">No scores yet.</p>
            ) : (
              <ul className="lb-list">
                {displayRows.map((r) => {
                  const isYou =
                    currentUserId &&
                    r.userId &&
                    String(r.userId) === String(currentUserId);
                  return (
                    <li
                      key={r.isPinnedViewer ? `pinned-${r.key}` : r.key}
                      className={`lb-row${r.rank === 1 ? " lb-row--first" : ""}${
                        isYou ? " lb-row--you" : ""
                      }${r.isPinnedViewer ? " lb-row--pinned" : ""}`}
                    >
                      <div className="lb-row__rank">
                        <RankMedal rank={r.rank} />
                      </div>
                      <div className="lb-row__name">
                        <span className="lb-row__name-text">{r.name}</span>
                        {isYou ? <span className="lb-you-badge">You</span> : null}
                      </div>
                      <div className="lb-row__time">{r.time}</div>
                      <div className="lb-row__points">
                        <span className="lb-points">{r.points.toLocaleString()}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default Leaderboard;
